import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowUpRight, Terminal } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const IMG_HERO = `${process.env.REACT_APP_BACKEND_URL}/api/files/marcuscylar/covers/9bbf0bba-7ad8-45fd-8a5b-7f5a92bd5ccf.jpeg`;

export default function Home() {
  const { content, services, talks, posts } = useContent();
  const c = content || {};
  const featuredPosts = posts.slice(0, 2);

  const coverSrc = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-backdrop opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-8"
              >
                <Terminal className="w-3.5 h-3.5" />
                <span>[ 01 · portfolio.mc ]</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight"
                data-testid="hero-name"
              >
                {c.hero_name || "Marcus Cylar"}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-6"
              >
                <span className="font-serif italic text-2xl sm:text-3xl text-accent" data-testid="hero-tagline">
                  {c.hero_tagline || "Ministry. Cybersecurity."}
                </span>
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed"
                data-testid="hero-subtitle"
              >
                {c.hero_subtitle}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <Link
                  to="/about"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em] hover:opacity-90 transition-opacity duration-200"
                >
                  {c.hero_cta_primary || "Read my story"}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] hover:bg-secondary transition-colors duration-200"
                >
                  {c.hero_cta_secondary || "Book me to speak"}
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="lg:col-span-4 relative"
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded-2xl border border-border">
                <img
                  src={IMG_HERO}
                  alt="Marcus Cylar"
                  className="w-full h-full object-cover object-[center_20%]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/90">
                  <div>[ speaker · strategist ]</div>
                  <div className="mt-1">DFIR → GRC</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="border-y border-border py-4 bg-secondary/40" data-testid="hero-marquee">
          <Marquee gradient={false} speed={30} pauseOnHover>
            {["DFIR", "GRC", "MINISTRY", "STRATEGY", "AWARENESS", "TABLETOPS", "NIST CSF", "ISO 27001", "CIS CONTROLS", "HUMAN-IN-THE-LOOP"].map((t, i) => (
              <span key={i} className="font-mono text-xs uppercase tracking-[0.3em] mx-8 text-muted-foreground">
                {t} <span className="text-accent mx-2">//</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="services-preview">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">[ 03 · what i do ]</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Services, translated for humans.</h2>
          </div>
          <Link to="/services" className="hidden sm:inline-flex items-center gap-1 text-sm link-underline">
            All services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-12">
          {services.slice(0, 4).map((s, idx) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className={`group relative p-8 rounded-2xl border border-border bg-card hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 ${
                idx === 0 ? "md:col-span-7" : idx === 1 ? "md:col-span-5" : idx === 2 ? "md:col-span-5" : "md:col-span-7"
              }`}
              data-testid={`service-card-${idx}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
                [ 0{idx + 1} ]
              </div>
              <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
              <div className="font-mono text-xs text-accent uppercase tracking-wider mb-4">{s.tagline}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SPEAKING PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-border">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">[ 04 · from the stage ]</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">Cyber stories that move rooms.</h2>
            <p className="mt-6 text-muted-foreground">
              Three-time Simply Cyber Con speaker, ministerial teacher, GRC evangelist. I translate complex risk into decisions your people can act on.
            </p>
            <Link to="/speaking" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] link-underline">
              View all talks <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {talks.slice(0, 3).map((t, i) => (
              <div key={t.id} className="group p-6 rounded-xl border border-border hover:border-foreground/40 transition-colors duration-200" data-testid={`talk-preview-${i}`}>
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h3 className="font-serif text-xl">{t.title}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t.date}</span>
                </div>
                <div className="font-mono text-xs text-accent uppercase tracking-wider mt-1">{t.venue}</div>
                <p className="text-sm text-muted-foreground mt-3">{t.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY INSIGHTS PREVIEW */}
      {featuredPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-border" data-testid="home-insights-preview">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">[ 05 · insights ]</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight max-w-2xl">Tips on cyber resilience, fresh from the field.</h2>
            </div>
            <Link to="/insights" className="inline-flex items-center gap-1 text-sm link-underline font-mono uppercase tracking-[0.2em] text-xs">
              All insights <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {featuredPosts.map((p, i) => {
              const cover = coverSrc(p.cover_image);
              return (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group relative rounded-2xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                  data-testid={`home-insight-card-${i}`}
                >
                  {cover ? (
                    <Link to={`/insights/${p.slug}`} className="block overflow-hidden">
                      <img src={cover} alt="" className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                    </Link>
                  ) : (
                    <div className="h-32 bg-secondary/40 grid-backdrop border-b border-border" />
                  )}
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">{p.tag}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-muted-foreground text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <Link
                      to={`/insights/${p.slug}`}
                      className="mt-6 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] link-underline"
                      data-testid={`home-insight-read-${i}`}
                    >
                      Read <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
